import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  PencilSquareIcon,
  ArchiveBoxIcon,
  XMarkIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'

import { formatSydneyDate, formatSydneyDateTime } from '../../utils/sydneyDateTime'
const STATUS_CONFIG = {
  open:        { label: 'Open',        bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  mitigating:  { label: 'Mitigating',  bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  closed:      { label: 'Closed',      bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  archived:    { label: 'Archived',    bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
}

const CONTROL_TYPE_LABEL = {
  rule: 'Monitor-Safe rule',
  cdd_trigger: 'CDD trigger',
  ecdd_trigger: 'ECDD trigger',
  policy: 'Policy clause',
  training: 'Training module',
  other: 'Other',
}

const CLOSURE_REASON_LABEL = {
  mitigated: 'Mitigated',
  accepted: 'Accepted (retained risk)',
  transferred: 'Transferred',
  no_longer_applicable: 'No longer applicable',
  superseded: 'Superseded',
}

const REOPEN_REASON_LABEL = {
  new_evidence: 'New evidence',
  scope_change: 'Scope change',
  regulatory_change: 'Regulatory / policy change',
  other: 'Other',
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' }
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      {cfg.label}
    </span>
  )
}

function scoreColor(s) {
  if (s == null) return '#9ca3af'
  const n = Number(s)
  if (n >= 16) return '#991b1b'
  if (n >= 9) return '#92400e'
  if (n >= 4) return '#1e40af'
  return '#166534'
}

function ScorePill({ label, value, tooltip }) {
  const c = scoreColor(value)
  return (
    <div
      title={tooltip}
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-mono font-semibold"
      style={{ backgroundColor: `${c}1a`, color: c, border: `1px solid ${c}33` }}
    >
      <span className="uppercase text-xs opacity-80">{label}</span>
      <span>{value == null ? '—' : value}</span>
    </div>
  )
}

function DataRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-800 text-right ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</span>
    </div>
  )
}

function formatDate(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : formatSydneyDate(d)
}

function formatDateTime(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : formatSydneyDateTime(d)
}

const CATEGORY_OPTIONS = ['AML', 'CTF', 'PF', 'RG', 'Fraud', 'Privacy', 'Other']

function ModalShell({ open, onClose, title, children, footer }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl border border-gray-100 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">{children}</div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">{footer}</div>
      </div>
    </div>
  )
}

function FieldLabel({ htmlFor, children, required }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div className="p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
      <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{msg}</span>
    </div>
  )
}

/** Edit title, description, category, typology. */
function EditNarrativeModal({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', category: '', typology_ref: '' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (open && item) {
      setForm({
        title: item.title || '',
        description: item.description || '',
        category: item.category || '',
        typology_ref: item.typology_ref || '',
      })
      setErr(null)
    }
  }, [open, item])

  const submit = useCallback(async () => {
    if (saving) return
    if (!form.title.trim()) { setErr('Title is required'); return }
    setSaving(true); setErr(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description.trim() || null,
          category: form.category || null,
          typology_ref: form.typology_ref.trim() || null,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      onSaved(json.data)
    } catch (e) { setErr(e.message || 'Save failed') }
    finally { setSaving(false) }
  }, [saving, form, item, onSaved])

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Edit narrative"
      footer={(
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" size="sm" className="bg-psg-teal hover:bg-psg-teal-700" onClick={submit} disabled={saving || !form.title.trim()}>
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    >
      <div>
        <FieldLabel htmlFor="edit-title" required>Title</FieldLabel>
        <input id="edit-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value.slice(0, 255) }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-gray-300" />
      </div>
      <div>
        <FieldLabel htmlFor="edit-desc">Description</FieldLabel>
        <textarea id="edit-desc" rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 8000) }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor="edit-cat">Category</FieldLabel>
          <select id="edit-cat" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
            <option value="">—</option>
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor="edit-typ">Typology ref</FieldLabel>
          <input id="edit-typ" value={form.typology_ref} onChange={(e) => setForm((f) => ({ ...f, typology_ref: e.target.value.slice(0, 64) }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300" />
        </div>
      </div>
      <ErrorBanner msg={err} />
    </ModalShell>
  )
}

/** Edit likelihood / impact / residual / status / mitigation_notes. */
function EditScoringModal({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({ likelihood: 3, impact: 3, residual_score: '', status: 'open', mitigation_notes: '' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (open && item) {
      setForm({
        likelihood: item.likelihood || 3,
        impact: item.impact || 3,
        residual_score: item.residual_score ?? '',
        status: item.status || 'open',
        mitigation_notes: item.mitigation_notes || '',
      })
      setErr(null)
    }
  }, [open, item])
  const inherent = Number(form.likelihood) * Number(form.impact)

  const submit = useCallback(async () => {
    if (saving) return
    setSaving(true); setErr(null)
    try {
      const body = {
        likelihood: Number(form.likelihood),
        impact: Number(form.impact),
        status: form.status,
        mitigation_notes: form.mitigation_notes.trim() || null,
      }
      if (form.residual_score === '' || form.residual_score == null) body.residual_score = null
      else body.residual_score = Number(form.residual_score)
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      onSaved(json.data)
    } catch (e) { setErr(e.message || 'Save failed') }
    finally { setSaving(false) }
  }, [saving, form, item, onSaved])

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Scoring & lifecycle"
      footer={(
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" size="sm" className="bg-psg-teal hover:bg-psg-teal-700" onClick={submit} disabled={saving}>
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    >
      <div className="grid grid-cols-3 gap-3">
        <div>
          <FieldLabel htmlFor="s-lik">Likelihood</FieldLabel>
          <select id="s-lik" value={form.likelihood} onChange={(e) => setForm((f) => ({ ...f, likelihood: Number(e.target.value) }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor="s-imp">Impact</FieldLabel>
          <select id="s-imp" value={form.impact} onChange={(e) => setForm((f) => ({ ...f, impact: Number(e.target.value) }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor="s-res">Residual</FieldLabel>
          <input id="s-res" type="number" min={0} max={25} value={form.residual_score} onChange={(e) => setForm((f) => ({ ...f, residual_score: e.target.value }))} placeholder="0–25" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300" />
        </div>
      </div>
      <div className="text-xs text-gray-500">
        Inherent ={' '}
        <span className="font-mono font-semibold text-gray-700">{inherent}</span>{' '}
        (computed). Residual reflects the score after current controls.
      </div>
      <div>
        <FieldLabel htmlFor="s-status">Status</FieldLabel>
        <select id="s-status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
          <option value="open">Open</option>
          <option value="mitigating">Mitigating</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          To close a risk, use <strong>Close risk</strong> on the detail page — a structured reason and narrative are required (S5-F1).
        </p>
      </div>
      <div>
        <FieldLabel htmlFor="s-notes">Mitigation notes</FieldLabel>
        <textarea id="s-notes" rows={4} value={form.mitigation_notes} onChange={(e) => setForm((f) => ({ ...f, mitigation_notes: e.target.value.slice(0, 8000) }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300" />
      </div>
      <ErrorBanner msg={err} />
    </ModalShell>
  )
}

/** Edit owner + review cadence + next review. */
function EditOwnershipModal({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({ owner_name: '', owner_id: '', review_cadence: '', next_review_at: '' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (open && item) {
      setForm({
        owner_name: item.owner_name || '',
        owner_id: item.owner_id || '',
        review_cadence: item.review_cadence || '',
        next_review_at: item.next_review_at ? String(item.next_review_at).slice(0, 10) : '',
      })
      setErr(null)
    }
  }, [open, item])

  const submit = useCallback(async () => {
    if (saving) return
    setSaving(true); setErr(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_name: form.owner_name.trim() || null,
          owner_id: form.owner_id.trim() || null,
          review_cadence: form.review_cadence || null,
          next_review_at: form.next_review_at || null,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      onSaved(json.data)
    } catch (e) { setErr(e.message || 'Save failed') }
    finally { setSaving(false) }
  }, [saving, form, item, onSaved])

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Ownership & review"
      footer={(
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" size="sm" className="bg-psg-teal hover:bg-psg-teal-700" onClick={submit} disabled={saving}>
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor="o-name">Owner name</FieldLabel>
          <input id="o-name" value={form.owner_name} onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value.slice(0, 128) }))} placeholder="e.g. Jane Smith (CO)" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-gray-300" />
        </div>
        <div>
          <FieldLabel htmlFor="o-id">Owner id</FieldLabel>
          <input id="o-id" value={form.owner_id} onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value.slice(0, 128) }))} placeholder="username / email" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor="o-cad">Review cadence</FieldLabel>
          <select id="o-cad" value={form.review_cadence} onChange={(e) => setForm((f) => ({ ...f, review_cadence: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
            <option value="">—</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div>
          <FieldLabel htmlFor="o-next">Next review date</FieldLabel>
          <input id="o-next" type="date" value={form.next_review_at} onChange={(e) => setForm((f) => ({ ...f, next_review_at: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-gray-300" />
        </div>
      </div>
      <ErrorBanner msg={err} />
    </ModalShell>
  )
}

/** AML/CTF Program v4.0 section ids (policy control_type). */
const AML_PROGRAM_POLICY_PRESETS = [
  { id: 'AML-CTF-4.0-CustomerTypes-1', label: 'Program § Customer types — 1 PEP' },
  { id: 'AML-CTF-4.0-CustomerTypes-3', label: 'Program § Customer types — 3 Red flags' },
  { id: 'AML-CTF-4.0-CustomerTypes-4', label: 'Program § Customer types — 4 Third-party cheque/EFT' },
  { id: 'AML-CTF-4.0-DesignatedServices-16', label: 'Program § Designated services — 16 Structuring' },
  { id: 'AML-CTF-4.0-DesignatedServices-14', label: 'Program § Designated services — 14 Minimal play' },
  { id: 'AML-CTF-4.0-DeliveryChannels-27', label: 'Program § Delivery — 27 Gaming income' },
  { id: 'AML-CTF-4.0-DeliveryChannels-28', label: 'Program § Delivery — 28 CardIT/TITO/cashier' },
  { id: 'PAYOUT-NO-THIRD-PARTY-CHEQUE', label: 'Payout policy — no third-party cheque' },
  { id: 'TTR-THRESHOLD-5000', label: 'TTR / large win identification' },
  { id: 'GOV-AML-INDUCTION', label: 'AML induction training (alias)' },
]

const ECDD_PRESETS = [
  { id: 'ECDD-PEP-IDENTIFIED', label: 'ECDD — PEP identified' },
  { id: 'ECDD-RED-FLAG-PATRON', label: 'ECDD — patron red flags' },
  { id: 'ECDD-SANCTIONS-MATCH', label: 'ECDD — sanctions match' },
]

const CONTROL_TYPE_OPTIONS = [
  { value: 'rule',          label: 'Monitor-Safe rule',
    hint: 'Pick a published rule from Monitor Safe, or enter a rule_id manually.' },
  { value: 'cdd_trigger',   label: 'CDD trigger',
    hint: 'Initial/Ongoing CDD trigger id. Lands fully with S2 three-tier CDD.' },
  { value: 'ecdd_trigger',  label: 'ECDD trigger',
    hint: 'ECDD trigger id (pep, screening, behavioural, …). Lands fully with S7.' },
  { value: 'policy',        label: 'Policy clause',
    hint: 'Reference to an AML/CTF Program clause (e.g. SEC-3.2).' },
  { value: 'training',      label: 'Training module',
    hint: 'Training module id from Governance Safe.' },
  { value: 'other',         label: 'Other',
    hint: 'Anything else — keep control_id human-readable.' },
]

function programPolicyIdForRisk(item) {
  const ref = item?.metadata?.aml_program_ref
  if (ref == null) return ''
  const section = (item.metadata?.aml_program_section || 'Customer types').replace(/\s+/g, '')
  if (String(section).toLowerCase().includes('delivery')) {
    return `AML-CTF-4.0-DeliveryChannels-${ref}`
  }
  if (String(section).toLowerCase().includes('designated')) {
    return `AML-CTF-4.0-DesignatedServices-${ref}`
  }
  return `AML-CTF-4.0-CustomerTypes-${ref}`
}

/** Link a control to a risk item. */
function AddControlLinkModal({ open, onClose, riskId, riskItem, onAdded }) {
  const [form, setForm] = useState({ control_type: 'rule', control_id: '', rationale: '' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [rules, setRules] = useState([])
  const [rulesLoading, setRulesLoading] = useState(false)

  useEffect(() => {
    if (open) {
      const suggestedPolicy = programPolicyIdForRisk(riskItem)
      setForm({
        control_type: 'policy',
        control_id: suggestedPolicy,
        rationale: riskItem?.metadata?.aml_program_ref
          ? `Mitigates AML/CTF Program v4.0 ref ${riskItem.metadata.aml_program_ref} (${riskItem.metadata.aml_program_section || 'risk assessment'}). `
          : '',
      })
      setErr(null)
    }
  }, [open, riskItem])

  useEffect(() => {
    if (!open || form.control_type !== 'rule') return
    let cancelled = false
    setRulesLoading(true)
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/rules/db?status=published'))
        const json = await res.json().catch(() => ({}))
        if (!cancelled && json.success && Array.isArray(json.data)) {
          setRules(json.data.map((r) => ({
            id: r.rule_id || r.id,
            name: r.name || r.rule_id || r.id,
            category: r.category,
          })).filter((r) => r.id))
        }
      } catch {
        if (!cancelled) setRules([])
      } finally {
        if (!cancelled) setRulesLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [open, form.control_type])

  const submit = useCallback(async () => {
    if (saving) return
    if (!form.control_id.trim()) { setErr('Control id is required'); return }
    if (form.rationale.trim().length < 20) { setErr('Rationale must be at least 20 characters'); return }
    setSaving(true); setErr(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(riskId)}/links`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          control_type: form.control_type,
          control_id: form.control_id.trim(),
          rationale: form.rationale.trim(),
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      onAdded(Array.isArray(json.data) ? json.data : null)
    } catch (e) { setErr(e.message || 'Add link failed') }
    finally { setSaving(false) }
  }, [saving, form, riskId, onAdded])

  const activeHint = CONTROL_TYPE_OPTIONS.find((o) => o.value === form.control_type)?.hint

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Link a control to this risk"
      footer={(
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" size="sm" className="bg-psg-teal hover:bg-psg-teal-700" onClick={submit} disabled={saving || !form.control_id.trim() || form.rationale.trim().length < 20}>
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />{saving ? 'Adding…' : 'Add link'}
          </Button>
        </>
      )}
    >
      <div>
        <FieldLabel htmlFor="l-type" required>Control type</FieldLabel>
        <select
          id="l-type"
          value={form.control_type}
          onChange={(e) => setForm((f) => ({ ...f, control_type: e.target.value }))}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          {CONTROL_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {activeHint && <p className="text-xs text-gray-500 mt-1">{activeHint}</p>}
      </div>
      <div>
        <FieldLabel htmlFor="l-id" required>Control id</FieldLabel>
        {form.control_type === 'rule' && rules.length > 0 ? (
          <select
            id="l-id"
            value={form.control_id}
            onChange={(e) => setForm((f) => ({ ...f, control_id: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono bg-white"
          >
            <option value="">Select a rule…</option>
            {rules.map((r) => (
              <option key={r.id} value={r.id}>{r.id} — {r.name}{r.category ? ` (${r.category})` : ''}</option>
            ))}
          </select>
        ) : form.control_type === 'policy' ? (
          <>
            <select
              id="l-id"
              value={AML_PROGRAM_POLICY_PRESETS.some((p) => p.id === form.control_id) ? form.control_id : '__custom__'}
              onChange={(e) => {
                const v = e.target.value
                setForm((f) => ({ ...f, control_id: v === '__custom__' ? '' : v }))
              }}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white mb-2"
            >
              <option value="">Select program section / policy…</option>
              {AML_PROGRAM_POLICY_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
              <option value="__custom__">Other (type below)</option>
            </select>
            <input
              value={form.control_id}
              onChange={(e) => setForm((f) => ({ ...f, control_id: e.target.value.slice(0, 128) }))}
              placeholder="e.g. AML-CTF-4.0-CustomerTypes-1 or internal policy code"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300"
            />
          </>
        ) : form.control_type === 'ecdd_trigger' ? (
          <>
            <select
              id="l-id"
              value={ECDD_PRESETS.some((p) => p.id === form.control_id) ? form.control_id : '__custom__'}
              onChange={(e) => {
                const v = e.target.value
                setForm((f) => ({ ...f, control_id: v === '__custom__' ? '' : v }))
              }}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white mb-2"
            >
              <option value="">Select ECDD trigger…</option>
              {ECDD_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
              <option value="__custom__">Other (type below)</option>
            </select>
            <input
              value={form.control_id}
              onChange={(e) => setForm((f) => ({ ...f, control_id: e.target.value.slice(0, 128) }))}
              placeholder="ECDD trigger id"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300"
            />
          </>
        ) : form.control_type === 'training' ? (
          <input
            id="l-id"
            value={form.control_id}
            onChange={(e) => setForm((f) => ({ ...f, control_id: e.target.value.slice(0, 128) }))}
            placeholder="e.g. GOV-AML-INDUCTION"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300"
          />
        ) : (
          <input
            id="l-id"
            value={form.control_id}
            onChange={(e) => setForm((f) => ({ ...f, control_id: e.target.value.slice(0, 128) }))}
            placeholder="Control reference id"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-gray-300"
            autoFocus
          />
        )}
        {form.control_type === 'rule' && rulesLoading && (
          <p className="text-xs text-gray-500 mt-1">Loading Monitor-Safe rules…</p>
        )}
      </div>
      <div>
        <FieldLabel htmlFor="l-rat" required>Rationale</FieldLabel>
        <textarea
          id="l-rat"
          rows={3}
          value={form.rationale}
          onChange={(e) => setForm((f) => ({ ...f, rationale: e.target.value.slice(0, 4000) }))}
          placeholder="Minimum 20 characters — how this control mitigates or maps to this risk (required for structured closure)."
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300"
        />
        <p className="text-xs text-gray-500 mt-1">{form.rationale.trim().length}/20+ characters</p>
      </div>
      <ErrorBanner msg={err} />
    </ModalShell>
  )
}

function linksWithAuthorRationaleCount(controls) {
  return (controls || []).filter((c) => c.rationale && String(c.rationale).trim().length >= 20).length
}

/** Structured close — requires closure_reason_code + narrative; API enforces controls & mitigated residual. */
function CloseRiskModal({ open, onClose, item, onSaved }) {
  const controls = Array.isArray(item?.controls) ? item.controls : []
  const strongLinks = linksWithAuthorRationaleCount(controls)
  const [reason, setReason] = useState('mitigated')
  const [narrative, setNarrative] = useState('')
  const [supersededId, setSupersededId] = useState('')
  const [residualInput, setResidualInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (open && item) {
      setReason('mitigated')
      setNarrative('')
      setSupersededId('')
      setResidualInput(item.residual_score != null ? String(item.residual_score) : '')
      setErr('')
    }
  }, [open, item])

  const needsStrongLinks = reason === 'mitigated' || reason === 'transferred' || reason === 'superseded'
  const warnLinks = needsStrongLinks && strongLinks < 1

  const submit = useCallback(async () => {
    if (!item || saving) return
    if (narrative.trim().length < 20) {
      setErr('Closure narrative must be at least 20 characters.')
      return
    }
    if (reason === 'superseded' && !supersededId.trim()) {
      setErr('Enter the risk id that supersedes this one (e.g. RA-2026-0006).')
      return
    }
    setSaving(true)
    setErr('')
    try {
      const body = {
        status: 'closed',
        closure_reason_code: reason,
        closure_narrative: narrative.trim(),
      }
      if (reason === 'superseded') body.superseded_by_risk_id = supersededId.trim()
      if (residualInput !== '' && residualInput != null) {
        const n = Number(residualInput)
        if (!Number.isNaN(n)) body.residual_score = n
      }
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        const msg = json.error || `HTTP ${res.status}`
        const code = json.code ? ` [${json.code}]` : ''
        throw new Error(`${msg}${code}`)
      }
      onSaved(json.data)
      onClose()
    } catch (e) {
      setErr(e.message || 'Close failed')
    } finally {
      setSaving(false)
    }
  }, [item, saving, reason, narrative, supersededId, residualInput, onSaved, onClose])

  if (!item) return null

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Close risk item"
      footer={(
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" size="sm" className="bg-psg-teal hover:bg-psg-teal-700" onClick={submit} disabled={saving}>
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />{saving ? 'Closing…' : 'Close risk'}
          </Button>
        </>
      )}
    >
      <p className="text-sm text-gray-600">
        Structured closure (S5-F1). Mitigated closes require residual within policy tolerance and at least one linked control with a 20+ character rationale.
      </p>
      {warnLinks && (
        <div className="p-2.5 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 flex gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <span>No linked control meets the rationale requirement yet. Add a link under <strong>Linked controls</strong> before closing with this reason.</span>
        </div>
      )}
      <div>
        <FieldLabel htmlFor="close-reason" required>Closure reason</FieldLabel>
        <select
          id="close-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="mitigated">Mitigated — residual within tolerance, controls operating</option>
          <option value="accepted">Accepted — senior sign-off; risk consciously retained</option>
          <option value="transferred">Transferred — insured / outsourced / contractual transfer</option>
          <option value="no_longer_applicable">No longer applicable — underlying activity ceased</option>
          <option value="superseded">Superseded — replaced by another risk item</option>
        </select>
      </div>
      {reason === 'superseded' && (
        <div>
          <FieldLabel htmlFor="close-super" required>Superseded by (risk id)</FieldLabel>
          <input
            id="close-super"
            value={supersededId}
            onChange={(e) => setSupersededId(e.target.value.slice(0, 32))}
            placeholder="RA-2026-…"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
          />
        </div>
      )}
      {reason === 'mitigated' && (
        <div>
          <FieldLabel htmlFor="close-res">Residual score (0–25)</FieldLabel>
          <input
            id="close-res"
            type="number"
            min={0}
            max={25}
            value={residualInput}
            onChange={(e) => setResidualInput(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
          />
          <p className="text-xs text-gray-500 mt-1">
            Server default max for mitigated is <strong>8</strong> unless env <code className="bg-gray-100 px-1 rounded">RISK_MITIGATED_MAX_RESIDUAL</code> is set.
          </p>
        </div>
      )}
      <div>
        <FieldLabel htmlFor="close-nar" required>Closure narrative</FieldLabel>
        <textarea
          id="close-nar"
          rows={5}
          value={narrative}
          onChange={(e) => setNarrative(e.target.value.slice(0, 8000))}
          placeholder="Evidence and reasoning supporting this closure (min 20 characters)."
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-y"
        />
      </div>
      <ErrorBanner msg={err} />
    </ModalShell>
  )
}

function ReopenRiskModal({ open, onClose, item, onSaved }) {
  const [reason, setReason] = useState('new_evidence')
  const [targetStatus, setTargetStatus] = useState('open')
  const [narrative, setNarrative] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (open) {
      setReason('new_evidence')
      setTargetStatus('open')
      setNarrative('')
      setErr('')
    }
  }, [open])

  const submit = useCallback(async () => {
    if (!item || saving) return
    if (narrative.trim().length < 20) {
      setErr('Reopen narrative must be at least 20 characters.')
      return
    }
    setSaving(true)
    setErr('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: targetStatus,
          reopen_reason_code: reason,
          reopen_narrative: narrative.trim(),
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        const msg = json.error || `HTTP ${res.status}`
        const code = json.code ? ` [${json.code}]` : ''
        throw new Error(`${msg}${code}`)
      }
      onSaved(json.data)
      onClose()
    } catch (e) {
      setErr(e.message || 'Reopen failed')
    } finally {
      setSaving(false)
    }
  }, [item, saving, reason, targetStatus, narrative, onSaved, onClose])

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Reopen risk item"
      footer={(
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" size="sm" className="bg-psg-teal hover:bg-psg-teal-700" onClick={submit} disabled={saving}>
            <CheckCircleIcon className="w-4 h-4 mr-1.5" />{saving ? 'Reopening…' : 'Reopen'}
          </Button>
        </>
      )}
    >
      <p className="text-sm text-gray-600">Record why this risk returns to the active register (min 20 characters).</p>
      <div>
        <FieldLabel htmlFor="reopen-status" required>Reopen as</FieldLabel>
        <select
          id="reopen-status"
          value={targetStatus}
          onChange={(e) => setTargetStatus(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="open">Open</option>
          <option value="mitigating">Mitigating</option>
        </select>
      </div>
      <div>
        <FieldLabel htmlFor="reopen-reason" required>Reason</FieldLabel>
        <select
          id="reopen-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="new_evidence">New evidence</option>
          <option value="scope_change">Scope change</option>
          <option value="regulatory_change">Regulatory / policy change</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div>
        <FieldLabel htmlFor="reopen-nar" required>Narrative</FieldLabel>
        <textarea
          id="reopen-nar"
          rows={4}
          value={narrative}
          onChange={(e) => setNarrative(e.target.value.slice(0, 8000))}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-y"
        />
      </div>
      <ErrorBanner msg={err} />
    </ModalShell>
  )
}

export default function RiskRegisterDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editNarrative, setEditNarrative] = useState(false)
  const [editScoring, setEditScoring] = useState(false)
  const [editOwnership, setEditOwnership] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [archiveErr, setArchiveErr] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [addLinkOpen, setAddLinkOpen] = useState(false)
  const [removingLinkId, setRemovingLinkId] = useState(null)
  const [linkErr, setLinkErr] = useState(null)
  const [closeRiskOpen, setCloseRiskOpen] = useState(false)
  const [reopenRiskOpen, setReopenRiskOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(id)}`))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setItem(json.data)
    } catch (e) {
      setError(e.message || 'Failed to load risk item')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const onSaved = useCallback((updated) => {
    if (updated) setItem((prev) => (prev ? { ...prev, ...updated } : updated))
    setEditNarrative(false); setEditScoring(false); setEditOwnership(false)
    // Refresh to pick up server-computed fields (inherent_score, updated_at).
    load()
  }, [load])

  const handleLinkAdded = useCallback((newControls) => {
    setAddLinkOpen(false)
    setLinkErr(null)
    if (Array.isArray(newControls)) {
      setItem((prev) => (prev ? { ...prev, controls: newControls } : prev))
    } else {
      load()
    }
  }, [load])

  const handleRemoveLink = useCallback(async (linkId, controlId) => {
    if (removingLinkId) return
    if (!confirm(`Remove the link to ${controlId}?`)) return
    setRemovingLinkId(linkId)
    setLinkErr(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}/links/${encodeURIComponent(linkId)}`),
        { method: 'DELETE' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setItem((prev) => prev ? { ...prev, controls: (prev.controls || []).filter((c) => c.id !== linkId) } : prev)
    } catch (e) {
      setLinkErr(e.message || 'Remove link failed')
    } finally {
      setRemovingLinkId(null)
    }
  }, [item, removingLinkId])

  const handleArchive = useCallback(async () => {
    if (!item || archiving) return
    if (!confirm(`Archive ${item.id}? It will stop appearing in the default list but remains auditable.`)) return
    setArchiving(true); setArchiveErr(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}`), { method: 'DELETE' })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      navigate('/governance-safe/risk-register')
    } catch (e) {
      setArchiveErr(e.message || 'Archive failed')
    } finally {
      setArchiving(false)
    }
  }, [item, archiving, navigate])

  const handleDelete = useCallback(async () => {
    if (!item || deleting) return
    if (!confirm(`Permanently delete ${item.id} — ${item.title}?\n\nThis cannot be undone. Linked controls will be removed with it.`)) return
    setDeleting(true)
    setArchiveErr(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/risk-items/${encodeURIComponent(item.id)}/permanent`),
        { method: 'DELETE' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      navigate('/governance-safe/risk-register')
    } catch (e) {
      setArchiveErr(e.message || 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }, [item, deleting, navigate])

  if (loading) {
    return (
      <div className="space-y-6">
        <Link to="/governance-safe/risk-register" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeftIcon className="w-4 h-4" /> Back to Risk Register
        </Link>
        <div className="py-12 text-center text-gray-500">Loading…</div>
      </div>
    )
  }

  if (error || !item) {
    return (
      <div className="space-y-6">
        <Link to="/governance-safe/risk-register" className="inline-flex items-center gap-2 text-sm font-medium text-psg-teal hover:underline">
          <ChevronLeftIcon className="w-4 h-4" /> Back to Risk Register
        </Link>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <ExclamationTriangleIcon className="mx-auto mb-2 w-8 h-8 text-red-500" />
            <h2 className="text-lg font-semibold text-red-700">Risk item not found</h2>
            <p className="text-sm text-red-600 mt-1">{error || `No record found with id: ${id}`}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const controls = Array.isArray(item.controls) ? item.controls : []

  return (
    <div className="space-y-5">
      <Link to="/governance-safe/risk-register" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
        <ChevronLeftIcon className="w-4 h-4" /> Back to Risk Register
      </Link>

      {/* Header card */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <ClipboardDocumentListIcon className="w-5 h-5 text-psg-teal" />
                <h2 className="text-xl font-bold text-psg-teal m-0">{item.id}</h2>
                <StatusBadge status={item.status} />
                {item.category && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                    {item.category}
                  </span>
                )}
                {item.typology_ref && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-slate-100 text-slate-700">
                    {item.typology_ref}
                  </span>
                )}
              </div>
              <div className="flex items-start gap-2 mt-2">
                <h3 className="text-lg font-semibold text-gray-900 m-0">{item.title}</h3>
                {item.status !== 'archived' && (
                  <button
                    type="button"
                    className="text-xs text-psg-teal hover:underline inline-flex items-center gap-1 mt-1"
                    onClick={() => setEditNarrative(true)}
                  >
                    <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap leading-relaxed">{item.description}</p>
              )}
            </div>
            <div className="shrink-0 flex flex-col items-end gap-2">
              <div className="flex items-center gap-1.5">
                <ScorePill label="I" value={item.inherent_score} tooltip="Inherent score = likelihood × impact" />
                <ScorePill label="R" value={item.residual_score} tooltip="Residual score — after controls" />
              </div>
              <div className="text-xs text-gray-500 font-mono">
                L {item.likelihood} · I {item.impact}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold text-psg-teal">Mitigation notes</div>
                {item.status !== 'closed' && item.status !== 'archived' && (
                  <button
                    type="button"
                    className="text-xs text-psg-teal hover:underline inline-flex items-center gap-1"
                    onClick={() => setEditScoring(true)}
                  >
                    <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>
              {item.mitigation_notes ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{item.mitigation_notes}</p>
              ) : (
                <p className="text-sm text-gray-400">
                  No mitigation notes recorded yet. Describe the controls in plain language here; link to
                  the specific controls in the panel on the right for the traceability graph.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-psg-teal" />
                  <span className="text-sm font-bold text-psg-teal">Linked controls</span>
                  {controls.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-psg-teal/10 text-psg-teal">
                      {controls.length}
                    </span>
                  )}
                </div>
                {item.status !== 'archived' && item.status !== 'closed' && (
                  <button
                    type="button"
                    className="text-xs text-psg-teal hover:underline inline-flex items-center gap-1"
                    onClick={() => setAddLinkOpen(true)}
                  >
                    <PlusIcon className="w-3.5 h-3.5" /> Add link
                  </button>
                )}
              </div>
              {linkErr && (
                <div className="mb-2 p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{linkErr}</span>
                </div>
              )}
              {controls.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No controls linked yet. A link says "this rule / policy / training exists because of this risk" —
                  an assessor can then thread from any control back to the risk it's there to address.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {controls.map((c) => (
                    <li key={c.id} className="py-2 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {CONTROL_TYPE_LABEL[c.control_type] || c.control_type}
                          </span>
                          {c.control_type === 'rule' ? (
                            <Link
                              to={`/monitor-safe/rules?rule=${encodeURIComponent(c.control_id)}`}
                              className="font-mono text-sm text-psg-teal hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {c.control_id}
                            </Link>
                          ) : (
                            <span className="font-mono text-sm text-gray-900">{c.control_id}</span>
                          )}
                        </div>
                        {c.rationale && (
                          <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap">{c.rationale}</p>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          Linked {formatDateTime(c.created_at)}
                          {c.created_by_name || c.created_by ? <> · {c.created_by_name || c.created_by}</> : null}
                        </div>
                      </div>
                      {item.status !== 'archived' && item.status !== 'closed' && (
                        <button
                          type="button"
                          title="Remove link"
                          className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 disabled:opacity-50 shrink-0"
                          onClick={() => handleRemoveLink(c.id, c.control_id)}
                          disabled={removingLinkId === c.id}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold text-psg-teal">Ownership & review</div>
                <button
                  type="button"
                  className="text-xs text-psg-teal hover:underline inline-flex items-center gap-1"
                  onClick={() => setEditOwnership(true)}
                >
                  <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                </button>
              </div>
              <DataRow label="Owner" value={item.owner_name || item.owner_id} />
              <DataRow label="Review cadence" value={item.review_cadence} />
              <DataRow label="Next review" value={formatDate(item.next_review_at)} />
              <DataRow label="Venue" value={item.venue_id} mono />
              <DataRow label="Regime" value={item.regime_version} mono />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-bold text-psg-teal mb-2">Audit</div>
              <DataRow label="Created" value={formatDateTime(item.created_at)} />
              <DataRow label="Updated" value={formatDateTime(item.updated_at)} />
              <DataRow label="Created by" value={item.created_by_name || item.created_by} />
              {item.status === 'closed' && (item.closure_reason_code || item.closed_at) && (
                <>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-3 mb-1">Closure (S5-F1)</div>
                  <DataRow label="Reason" value={CLOSURE_REASON_LABEL[item.closure_reason_code] || item.closure_reason_code} />
                  <DataRow label="Narrative" value={item.closure_narrative} />
                  <DataRow label="Closed at" value={formatDateTime(item.closed_at)} />
                  <DataRow label="Closed by" value={item.closed_by_name || item.closed_by} />
                  {item.superseded_by_risk_id ? (
                    <DataRow
                      label="Superseded by"
                      value={(
                        <Link to={`/governance-safe/risk-register/${encodeURIComponent(item.superseded_by_risk_id)}`} className="text-psg-teal hover:underline font-mono">
                          {item.superseded_by_risk_id}
                        </Link>
                      )}
                    />
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>

          {Array.isArray(item.status_events) && item.status_events.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="text-sm font-bold text-psg-teal mb-2">Status history</div>
                <ul className="space-y-2 text-sm max-h-60 overflow-y-auto">
                  {item.status_events.map((ev) => (
                    <li key={ev.id} className="border-b border-gray-100 pb-2 last:border-0">
                      <div className="font-mono text-xs text-gray-500">{formatDateTime(ev.created_at)}</div>
                      <div className="text-gray-800">
                        <span className="font-semibold capitalize">{ev.event_kind}</span>
                        {' · '}
                        {ev.previous_status} → {ev.new_status}
                      </div>
                      {ev.reason_code && (
                        <div className="text-xs text-gray-600 mt-0.5">
                          {ev.event_kind === 'reopen'
                            ? (REOPEN_REASON_LABEL[ev.reason_code] || ev.reason_code)
                            : (CLOSURE_REASON_LABEL[ev.reason_code] || ev.reason_code)}
                          {ev.superseded_by_risk_id ? ` · → ${ev.superseded_by_risk_id}` : ''}
                        </div>
                      )}
                      {ev.narrative && <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{ev.narrative}</p>}
                      {(ev.actor_name || ev.actor_id) && (
                        <div className="text-xs text-gray-400 mt-0.5">{ev.actor_name || ev.actor_id}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {item.status !== 'archived' && (
            <Card>
              <CardContent className="p-4">
                <div className="text-sm font-bold text-psg-teal mb-2">Actions</div>
                <div className="space-y-2">
                  {(item.status === 'open' || item.status === 'mitigating') && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setEditScoring(true)}
                      >
                        <PencilSquareIcon className="w-4 h-4 mr-1.5" /> Scoring & lifecycle
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full border-green-200 text-green-800 hover:bg-green-50"
                        onClick={() => setCloseRiskOpen(true)}
                      >
                        <CheckCircleIcon className="w-4 h-4 mr-1.5" /> Close risk
                      </Button>
                    </>
                  )}
                  {item.status === 'closed' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full border-amber-200 text-amber-900 hover:bg-amber-50"
                      onClick={() => setReopenRiskOpen(true)}
                    >
                      Reopen risk
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full text-red-700 border-red-200 hover:bg-red-50"
                    onClick={handleArchive}
                    disabled={archiving || deleting}
                  >
                    <ArchiveBoxIcon className="w-4 h-4 mr-1.5" />
                    {archiving ? 'Archiving…' : 'Archive'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full text-red-700 border-red-200 hover:bg-red-50"
                    onClick={handleDelete}
                    disabled={archiving || deleting}
                  >
                    <TrashIcon className="w-4 h-4 mr-1.5" />
                    {deleting ? 'Deleting…' : 'Delete permanently'}
                  </Button>
                </div>
                {archiveErr && (
                  <div className="mt-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{archiveErr}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {item.status === 'archived' && (
            <Card>
              <CardContent className="p-4">
                <div className="text-sm font-bold text-psg-teal mb-2">Actions</div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-red-700 border-red-200 hover:bg-red-50"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  <TrashIcon className="w-4 h-4 mr-1.5" />
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </Button>
                {archiveErr && (
                  <div className="mt-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{archiveErr}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* C4 adds the Add-link panel in the left column. */}
        </div>
      </div>

      <EditNarrativeModal open={editNarrative} onClose={() => setEditNarrative(false)} item={item} onSaved={onSaved} />
      <EditScoringModal   open={editScoring}   onClose={() => setEditScoring(false)}   item={item} onSaved={onSaved} />
      <EditOwnershipModal open={editOwnership} onClose={() => setEditOwnership(false)} item={item} onSaved={onSaved} />
      <AddControlLinkModal
        open={addLinkOpen}
        onClose={() => setAddLinkOpen(false)}
        riskId={item.id}
        riskItem={item}
        onAdded={handleLinkAdded}
      />
      <CloseRiskModal open={closeRiskOpen} onClose={() => setCloseRiskOpen(false)} item={item} onSaved={onSaved} />
      <ReopenRiskModal open={reopenRiskOpen} onClose={() => setReopenRiskOpen(false)} item={item} onSaved={onSaved} />
    </div>
  )
}
